import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FiPlus, FiEdit2, FiTrash2, FiUpload, FiX } from 'react-icons/fi';
import {
  fetchInstantCakes,
  createInstantCake,
  updateInstantCake,
  deleteInstantCake,
  updateInstantCakeStock,
  refreshInstantCakeQrCode,
  adminInstantCakes,
  InstantCakeItem,
  InstantCakeInput,
} from '../../api/adminBubbleTeaOptions';
import axiosInstance from '../../api/axios';
import { formatCurrency } from '../../utils/currency';
import { localizeErrorDetail, getErrorDetail } from '../../utils/errorDetail';
import QrCodeField from '../../components/Admin/QrCodeField';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace('/api', '');
const resolveImageUrl = (url: string | null | undefined): string =>
  !url ? '' : (url.startsWith('data:') || url.startsWith('http')) ? url : `${API_ORIGIN}${url}`;

// ─── Styled Components (matching AdminBubbleTeaOptions palette) ───────────────

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #333;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const IconButton = styled.button<{ variant?: 'edit' | 'delete' }>`
  padding: 8px;
  background: ${props => props.variant === 'delete' ? '#fee2e2' : '#ede9fe'};
  color: ${props => props.variant === 'delete' ? '#dc2626' : '#667eea'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.variant === 'delete' ? '#fecaca' : '#ddd6fe'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: #f3f4f6;
  color: #6b7280;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #e5e7eb;
  }
`;

// ─── Card / Table ─────────────────────────────────────────────────────────────

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #333;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
`;

const Tr = styled.tr`
  transition: background 0.1s ease;

  &:hover {
    background: #fafafa;
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const ActionCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

// ─── Badges ───────────────────────────────────────────────────────────────────

const PriceBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  background: #ede9fe;
  color: #5b21b6;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
`;

const ActiveBadge = styled.span<{ active: boolean }>`
  display: inline-block;
  padding: 3px 8px;
  background: ${props => props.active ? '#d1fae5' : '#f3f4f6'};
  color: ${props => props.active ? '#065f46' : '#6b7280'};
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
`;

// ─── Toggle switch ────────────────────────────────────────────────────────────

const ToggleLabel = styled.label`
  position: relative;
  display: inline-block;
  width: 38px;
  height: 22px;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  span {
    position: absolute;
    inset: 0;
    background: #e5e7eb;
    border-radius: 22px;
    transition: background 0.2s ease;

    &::before {
      content: '';
      position: absolute;
      width: 16px;
      height: 16px;
      left: 3px;
      top: 3px;
      background: white;
      border-radius: 50%;
      transition: transform 0.2s ease;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    }
  }

  input:checked + span {
    background: #667eea;

    &::before {
      transform: translateX(16px);
    }
  }

  input:disabled + span {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ─── Stock inline edit ────────────────────────────────────────────────────────

const StockCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StockValue = styled.span`
  cursor: pointer;
  padding: 3px 8px;
  border-radius: 4px;
  background: #f3f4f6;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  min-width: 32px;
  text-align: center;
  transition: background 0.15s ease;

  &:hover {
    background: #e5e7eb;
  }
`;

const StockInput = styled.input`
  width: 64px;
  padding: 4px 8px;
  border: 1px solid #667eea;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  outline: none;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
`;

// ─── Image thumbnail ──────────────────────────────────────────────────────────

const Thumbnail = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
`;

const NoImage = styled.div`
  width: 40px;
  height: 40px;
  background: #f3f4f6;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #9ca3af;
`;

// ─── Image upload ─────────────────────────────────────────────────────────────

const ImageUploadArea = styled.div`
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  margin-bottom: 8px;
`;

const FileInputHidden = styled.input`
  display: none;
`;

const UploadButtonLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background: #e5e7eb;
    color: #4b5563;
  }
`;

const ImagePreviewWrap = styled.div`
  position: relative;
  display: inline-block;
  margin-top: 8px;
`;

const PreviewImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #e5e7eb;
`;

const RemoveImageBtn = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  background: rgba(0,0,0,0.6);
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  &:hover {
    background: rgba(0,0,0,0.85);
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

// ─── Empty state ──────────────────────────────────────────────────────────────

const EmptyCell = styled.td`
  padding: 40px 16px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
`;

// ─── Modal ────────────────────────────────────────────────────────────────────

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  padding: 24px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6b7280;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #333;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

// Places an EN FormGroup next to its paired ZH (Chinese) FormGroup, e.g. for
// Name / Name (Chinese). Mirrors AdminBubbleTeaOptions.tsx / AdminQBMenu.tsx's
// FormRow (task-025 story-004-6).
const FormRow = styled.div`
  display: flex;
  gap: 16px;

  ${FormGroup} {
    flex: 1;
    min-width: 0;
  }
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Textarea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const SubmitButton = styled(Button)`
  margin: 0;
`;

// ─── Loading / Error ──────────────────────────────────────────────────────────

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
`;

const Spinner = styled.div`
  border: 3px solid #e5e7eb;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #991b1b;
  border-radius: 8px;
  padding: 16px 20px;
  font-size: 14px;
  margin-bottom: 20px;
`;


// ─── Form state ───────────────────────────────────────────────────────────────

interface CakeFormState {
  name: string;
  // Simplified-Chinese translation of name (task-025 story-004-6). Optional --
  // mirrors backend InstantCakeItemResponse.name_zh in admin_bubbletea_options.py.
  name_zh: string;
  description: string;
  // Simplified-Chinese translation of description (task-025 story-004-6).
  description_zh: string;
  price: number;
  stock_count: number;
  image_url: string;
  active: boolean;
  category: string;
  qr_enabled: boolean;
  qr_token?: string | null;
}

// Instant Cakes default qr_enabled to FALSE for newly-created items, mirroring
// AdminProducts.tsx's Custom Cakes default (J40, off-by-default) -- NOT
// AdminQBMenu.tsx's Bubble Tea default of true (J39).
const BLANK_FORM: CakeFormState = {
  name: '',
  name_zh: '',
  description: '',
  description_zh: '',
  price: 0,
  stock_count: 0,
  image_url: '',
  active: true,
  category: '',
  qr_enabled: false,
  qr_token: null,
};

function formFromItem(item: InstantCakeItem): CakeFormState {
  return {
    name: item.name,
    name_zh: item.name_zh ?? '',
    description: item.description ?? '',
    description_zh: item.description_zh ?? '',
    price: item.price,
    stock_count: item.stock_count,
    image_url: item.image_url ?? '',
    active: item.active,
    category: item.category ?? '',
    qr_enabled: item.qr_enabled,
    qr_token: item.qr_token,
  };
}

// ─── Stock inline edit sub-component ─────────────────────────────────────────

interface StockEditorProps {
  item: InstantCakeItem;
  onSave: (id: number, value: number) => void;
  disabled: boolean;
}

const StockEditor: React.FC<StockEditorProps> = ({ item, onSave, disabled }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.stock_count);

  const commit = () => {
    const parsed = parseInt(String(draft), 10);
    const value = isNaN(parsed) || parsed < 0 ? 0 : parsed;
    setEditing(false);
    if (value !== item.stock_count) {
      onSave(item.id, value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') setEditing(false);
  };

  if (editing) {
    return (
      <StockCell>
        <StockInput
          type="number"
          min={0}
          value={draft}
          autoFocus
          onChange={e => setDraft(parseInt(e.target.value, 10))}
          onBlur={commit}
          onKeyDown={handleKeyDown}
        />
      </StockCell>
    );
  }

  return (
    <StockCell>
      <StockValue
        title={t('admin.instantCakes.stockEditTooltip')}
        onClick={() => { if (!disabled) { setDraft(item.stock_count); setEditing(true); } }}
      >
        {item.stock_count}
      </StockValue>
    </StockCell>
  );
};

// ─── Main page component ──────────────────────────────────────────────────────

const AdminInstantCakes: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InstantCakeItem | null>(null);
  const [form, setForm] = useState<CakeFormState>(BLANK_FORM);
  const [error, setError] = useState<string | null>(null);
  const [qrRefreshError, setQrRefreshError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Bumped on every modal-session boundary (open-add, open-edit, close) so an
  // in-flight create/update request from a now-stale session can identify
  // itself as stale in onSuccess/onError via the closure-captured sessionId,
  // and not misattribute its result to whatever session is open by the time
  // it resolves. Mirrors AdminQBMenu.tsx's sessionIdRef guard (task-025
  // Sprint 7 edge-case-auditor fix).
  const sessionIdRef = useRef(0);
  const [qrImageSrc, setQrImageSrc] = useState('');
  const [qrImageFailed, setQrImageFailed] = useState(false);
  const [qrRefreshing, setQrRefreshing] = useState(false);

  const { data: rawCakes, isLoading, isError } = useQuery({
    queryKey: ['instantCakes'],
    queryFn: fetchInstantCakes,
  });
  const cakes = Array.isArray(rawCakes) ? rawCakes : [];

  const { data: rawCatData } = useQuery<{ categories: { name: string; display_order: number }[] }>({
    queryKey: ['qbCategories'],
    queryFn: async () => {
      const res = await axiosInstance.get<{ categories: { name: string; display_order: number }[] }>('/admin/settings/qb-categories');
      return res.data;
    },
  });
  const categories: { name: string; display_order: number }[] = Array.isArray(rawCatData?.categories) ? rawCatData!.categories : [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['instantCakes'] });

  const createMutation = useMutation({
    mutationFn: ({ data }: { data: Partial<InstantCakeInput>; sessionId: number }) => createInstantCake(data),
    onSuccess: (_data, variables) => {
      invalidate();
      if (variables.sessionId === sessionIdRef.current) { closeModal(); }
    },
    onError: (err: any, variables) => {
      if (variables.sessionId === sessionIdRef.current) {
        setSaveError(localizeErrorDetail(getErrorDetail(err), t) || t('admin.instantCakes.genericError'));
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<InstantCakeInput>; sessionId: number }) =>
      updateInstantCake(id, data),
    onSuccess: (_data, variables) => {
      invalidate();
      if (variables.sessionId === sessionIdRef.current) { closeModal(); }
    },
    onError: (err: any, variables) => {
      if (variables.sessionId === sessionIdRef.current) {
        setSaveError(localizeErrorDetail(getErrorDetail(err), t) || t('admin.instantCakes.genericError'));
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteInstantCake(id),
    onSuccess: () => { setDeleteError(null); invalidate(); },
    onError: (err: any) => {
      setDeleteError(localizeErrorDetail(getErrorDetail(err), t) || t('admin.instantCakes.deleteFailedError'));
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      updateInstantCake(id, { active }),
    onSuccess: invalidate,
  });

  const stockMutation = useMutation({
    mutationFn: ({ id, stockCount }: { id: number; stockCount: number }) =>
      updateInstantCakeStock(id, stockCount),
    onSuccess: invalidate,
  });

  const processFile = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setForm(prev => ({ ...prev, image_url: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openAdd = () => {
    sessionIdRef.current += 1;
    setDeleteError(null);
    setEditingItem(null);
    setForm(BLANK_FORM);
    setModalOpen(true);
  };

  const openEdit = (item: InstantCakeItem) => {
    sessionIdRef.current += 1;
    setDeleteError(null);
    setEditingItem(item);
    setForm(formFromItem(item));
    setModalOpen(true);
  };

  const closeModal = () => {
    sessionIdRef.current += 1;
    setModalOpen(false);
    setEditingItem(null);
    setForm(BLANK_FORM);
    setError(null);
    setSaveError(null);
    setQrRefreshError(null);
    setQrImageFailed(false);
    setQrRefreshing(false);
  };

  // GET /admin/bubble-tea-options/instant-cakes/{id}/qr-image is admin-gated
  // via a Bearer token header (Depends(get_admin_user)), so a plain <img src>
  // would 401 -- there's no cookie-based session fallback. Fetch the PNG
  // through the authenticated axios client as a blob and hand QrCodeField an
  // object URL, mirroring AdminProducts.tsx's task-020-s4-1 pattern exactly
  // (see .orchestrator/context.md's Discovered section).
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    const loadQrImage = async () => {
      if (modalOpen && editingItem?.id && form.qr_enabled && form.qr_token) {
        setQrImageFailed(false);
        setQrImageSrc('');
        try {
          const blob = await adminInstantCakes.getQrImage(editingItem.id);
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          setQrImageSrc(objectUrl);
          setError(null);
        } catch (err) {
          console.error('Failed to load QR code image:', err);
          if (!cancelled) {
            setQrImageSrc('');
            setError(t('admin.instantCakes.qrImageLoadFailedError'));
            setQrImageFailed(true);
          }
        }
      } else {
        setQrImageSrc('');
        setQrImageFailed(false);
      }
    };

    loadQrImage();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [modalOpen, editingItem?.id, form.qr_enabled, form.qr_token]);

  const handleRefreshQrCode = async () => {
    if (!editingItem?.id || qrRefreshing) return;
    setQrRefreshing(true);
    setQrRefreshError(null);
    try {
      const updated = await refreshInstantCakeQrCode(editingItem.id);
      // Update just the token so the effect above reloads the image, without
      // a full instant-cake list re-fetch.
      setForm(prev => ({ ...prev, qr_token: updated.qr_token }));
    } catch (err) {
      console.error('Failed to refresh QR code:', err);
      setQrRefreshError(t('admin.instantCakes.qrRefreshFailedError'));
      try {
        const currentCakes = await fetchInstantCakes();
        const current = currentCakes.find((c: any) => c.id === editingItem?.id);
        if (current) {
          setForm(prev => ({ ...prev, qr_token: current.qr_token }));
        } else {
          setQrImageSrc('');
        }
      } catch (reconcileErr) {
        console.error('Failed to reconcile QR code state after refresh error:', reconcileErr);
        setQrImageSrc('');
      }
    } finally {
      setQrRefreshing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaveError(null);
    let imageUrl = form.image_url.trim() || null;
    if (imageUrl && imageUrl.startsWith('data:')) {
      try {
        const res = await axiosInstance.post<{ url: string }>('/admin/upload-base64', { image: imageUrl });
        imageUrl = res.data.url;
      } catch (err) {
        setError(t('admin.instantCakes.imageUploadFailedError'));
        return;
      }
    }
    const payload: Partial<InstantCakeInput> = {
      name: form.name,
      name_zh: form.name_zh.trim() || null,
      description: form.description,
      description_zh: form.description_zh.trim() || null,
      price: form.price,
      stock_count: form.stock_count,
      image_url: imageUrl,
      active: form.active,
      category: form.category.trim() || null,
      qr_enabled: form.qr_enabled,
    };
    const sessionId = sessionIdRef.current;
    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data: payload, sessionId });
    } else {
      createMutation.mutate({ data: payload, sessionId });
    }
  };

  const handleDelete = (item: InstantCakeItem) => {
    if (window.confirm(t('admin.instantCakes.deleteConfirm', { name: item.name }))) {
      setDeleteError(null);
      deleteMutation.mutate(item.id);
    }
  };

  const isMutating = createMutation.isPending || updateMutation.isPending;

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  if (isError) {
    return (
      <Container>
        <Header>
          <Title>{t('admin.instantCakes.title')}</Title>
        </Header>
        <ErrorMessage>{t('admin.instantCakes.loadError')}</ErrorMessage>
      </Container>
    );
  }

  return (
    <>
      <Container>
        <Header>
          <Title>{t('admin.instantCakes.title')}</Title>
          <Button onClick={openAdd}>
            <FiPlus size={14} />
            {t('admin.instantCakes.addButton')}
          </Button>
        </Header>

        {deleteError && (
          <p style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{deleteError}</p>
        )}

        <Card>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>{t('admin.instantCakes.imageHeader')}</Th>
                  <Th>{t('admin.instantCakes.nameHeader')}</Th>
                  <Th>{t('admin.instantCakes.descriptionHeader')}</Th>
                  <Th>{t('admin.instantCakes.priceHeader')}</Th>
                  <Th>{t('admin.instantCakes.categoryHeader')}</Th>
                  <Th>{t('admin.instantCakes.stockHeader')}</Th>
                  <Th>{t('admin.instantCakes.activeHeader')}</Th>
                  <Th>{t('admin.instantCakes.actionsHeader')}</Th>
                </tr>
              </thead>
              <tbody>
                {cakes.length === 0 ? (
                  <tr>
                    <EmptyCell colSpan={8}>
                      {t('admin.instantCakes.emptyState')}
                    </EmptyCell>
                  </tr>
                ) : (
                  cakes.map(cake => (
                    <Tr key={cake.id}>
                      <Td>
                        {cake.image_url
                          ? <Thumbnail src={resolveImageUrl(cake.image_url)} alt={cake.name} />
                          : <NoImage>{t('admin.instantCakes.noImageLabel')}</NoImage>
                        }
                      </Td>
                      <Td style={{ fontWeight: 600 }}>{cake.name}</Td>
                      <Td style={{ color: '#6b7280', maxWidth: 240 }}>
                        <span title={cake.description ?? ''}>
                          {(cake.description ?? '').length > 60
                            ? (cake.description ?? '').slice(0, 57) + '…'
                            : (cake.description ?? '')}
                        </span>
                      </Td>
                      <Td>
                        <PriceBadge>{formatCurrency(cake.price)}</PriceBadge>
                      </Td>
                      <Td>
                        {cake.category
                          ? <span style={{ padding: '3px 8px', background: '#dbeafe', color: '#1e40af', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{cake.category}</span>
                          : <span style={{ color: '#9ca3af' }}>—</span>
                        }
                      </Td>
                      <Td>
                        <StockEditor
                          item={cake}
                          onSave={(id, stockCount) => stockMutation.mutate({ id, stockCount })}
                          disabled={stockMutation.isPending}
                        />
                      </Td>
                      <Td>
                        <ToggleLabel title={cake.active ? t('admin.instantCakes.deactivateTooltip') : t('admin.instantCakes.activateTooltip')}>
                          <input
                            type="checkbox"
                            checked={cake.active}
                            disabled={toggleMutation.isPending}
                            onChange={() =>
                              toggleMutation.mutate({ id: cake.id, active: !cake.active })
                            }
                          />
                          <span />
                        </ToggleLabel>
                      </Td>
                      <Td>
                        <ActionCell>
                          <IconButton
                            variant="edit"
                            title={t('admin.instantCakes.editTooltip')}
                            onClick={() => openEdit(cake)}
                          >
                            <FiEdit2 size={14} />
                          </IconButton>
                          <IconButton
                            variant="delete"
                            title={t('admin.instantCakes.deleteTooltip')}
                            disabled={deleteMutation.isPending}
                            onClick={() => handleDelete(cake)}
                          >
                            <FiTrash2 size={14} />
                          </IconButton>
                        </ActionCell>
                      </Td>
                    </Tr>
                  ))
                )}
              </tbody>
            </Table>
          </TableWrapper>
        </Card>
      </Container>

      {/* Add / Edit modal */}
      <Modal isOpen={modalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingItem ? t('admin.instantCakes.modalEditTitle', { name: editingItem.name }) : t('admin.instantCakes.modalAddTitle')}
            </ModalTitle>
            <CloseButton onClick={closeModal}>×</CloseButton>
          </ModalHeader>

          <Form onSubmit={handleSubmit}>
            <FormRow>
              <FormGroup>
                <Label>{t('admin.instantCakes.nameLabel')}</Label>
                <Input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('admin.instantCakes.namePlaceholder')}
                  required
                  autoFocus
                />
              </FormGroup>

              <FormGroup>
                <Label>{t('admin.instantCakes.nameZhLabel')}</Label>
                <Input
                  type="text"
                  value={form.name_zh}
                  onChange={e => setForm(prev => ({ ...prev, name_zh: e.target.value }))}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>{t('admin.instantCakes.descriptionLabel')}</Label>
                <Textarea
                  value={form.description}
                  onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={t('admin.instantCakes.descriptionPlaceholder')}
                />
              </FormGroup>

              <FormGroup>
                <Label>{t('admin.instantCakes.descriptionZhLabel')}</Label>
                <Textarea
                  value={form.description_zh}
                  onChange={e => setForm(prev => ({ ...prev, description_zh: e.target.value }))}
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>{t('admin.instantCakes.priceLabel')}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.price}
                onChange={e =>
                  setForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))
                }
              />
            </FormGroup>

            <FormGroup>
              <Label>{t('admin.instantCakes.stockCountLabel')}</Label>
              <Input
                type="number"
                min="0"
                step="1"
                value={form.stock_count}
                onChange={e =>
                  setForm(prev => ({ ...prev, stock_count: parseInt(e.target.value, 10) || 0 }))
                }
              />
            </FormGroup>

            <FormGroup>
              <Label>{t('admin.instantCakes.categoryLabel')}</Label>
              <Select
                value={form.category}
                onChange={e => setForm(prev => ({ ...prev, category: e.target.value }))}
              >
                <option value="">{t('admin.instantCakes.noCategoryOption')}</option>
                {categories.map(cat => (
                  <option key={cat.name} value={cat.name}>{cat.name}</option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>{t('admin.instantCakes.imageLabel')}</Label>
              <ImageUploadArea>
                <FileInputHidden
                  ref={fileInputRef}
                  type="file"
                  id="instantCakeImageUpload"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <UploadButtonLabel htmlFor="instantCakeImageUpload">
                  <FiUpload size={14} />
                  {t('admin.instantCakes.uploadImageButton')}
                </UploadButtonLabel>
              </ImageUploadArea>
              {form.image_url && (
                <ImagePreviewWrap>
                  <PreviewImage
                    src={
                      form.image_url.startsWith('data:') || form.image_url.startsWith('http')
                        ? form.image_url
                        : `${(process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace('/api', '')}${form.image_url}`
                    }
                    alt={t('admin.imagePreviewAlt')}
                  />
                  <RemoveImageBtn
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, image_url: '' }))}
                  >
                    <FiX size={10} />
                  </RemoveImageBtn>
                </ImagePreviewWrap>
              )}
            </FormGroup>

            <FormGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={e => setForm(prev => ({ ...prev, active: e.target.checked }))}
                />
                {t('admin.instantCakes.activeCheckboxLabel')}
              </CheckboxLabel>
            </FormGroup>

            <FormGroup>
              <Label>{t('admin.instantCakes.qrCodeLabel')}</Label>
              <QrCodeField
                qrEnabled={form.qr_enabled ?? false}
                qrToken={form.qr_token}
                onQrEnabledChange={(enabled) => setForm(prev => ({ ...prev, qr_enabled: enabled }))}
                qrImageUrl={qrImageSrc}
                onRefresh={handleRefreshQrCode}
                itemActive={form.active}
                isRefreshing={qrRefreshing}
                failed={qrImageFailed}
              />
            </FormGroup>

            {error && (
              <ErrorMessage>{error}</ErrorMessage>
            )}
            {qrRefreshError && (
              <ErrorMessage>{qrRefreshError}</ErrorMessage>
            )}
            {!error && saveError && (
              <ErrorMessage>{saveError}</ErrorMessage>
            )}

            <ModalActions>
              <CancelButton type="button" onClick={closeModal}>
                {t('admin.instantCakes.cancelButton')}
              </CancelButton>
              <SubmitButton type="submit" disabled={isMutating}>
                {isMutating
                  ? (editingItem ? t('admin.instantCakes.savingButton') : t('admin.instantCakes.addingButton'))
                  : (editingItem ? t('admin.instantCakes.saveChangesButton') : t('admin.instantCakes.addSubmitButton'))}
              </SubmitButton>
            </ModalActions>
          </Form>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AdminInstantCakes;
